<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import ContactsAPI from 'dashboard/api/contacts';
import FollowUpSequencesAPI from 'dashboard/api/followUpSequences';

const props = defineProps({
  sequenceId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['enrolled']);

const { t } = useI18n();
const dialogRef = ref(null);
const searchQuery = ref('');
const searchResults = ref([]);
const selectedIds = ref(new Set());
const isSearching = ref(false);
const isEnrolling = ref(false);
const currentPage = ref(1);
const hasMore = ref(false);
const isLoadingMore = ref(false);
let searchTimeout = null;

const performSearch = async (page = 1, append = false) => {
  if (searchQuery.value.length < 2) {
    searchResults.value = [];
    hasMore.value = false;
    return;
  }

  if (page === 1) {
    isSearching.value = true;
  } else {
    isLoadingMore.value = true;
  }

  try {
    const response = await ContactsAPI.search(searchQuery.value, page);
    const payload = response.data?.payload || [];

    if (append) {
      searchResults.value = [...searchResults.value, ...payload];
    } else {
      searchResults.value = payload;
    }

    currentPage.value = page;
    // If we got a full page (25 items), there might be more
    hasMore.value = payload.length >= 25;
  } catch {
    if (!append) searchResults.value = [];
    hasMore.value = false;
  } finally {
    isSearching.value = false;
    isLoadingMore.value = false;
  }
};

const handleSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
    hasMore.value = false;
    performSearch(1, false);
  }, 300);
};

const loadMore = () => {
  if (isLoadingMore.value || !hasMore.value) return;
  performSearch(currentPage.value + 1, true);
};

const handleScroll = event => {
  const el = event.target;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
    loadMore();
  }
};

const toggleContact = contactId => {
  if (selectedIds.value.has(contactId)) {
    selectedIds.value.delete(contactId);
  } else {
    selectedIds.value.add(contactId);
  }
  // Force reactivity
  selectedIds.value = new Set(selectedIds.value);
};

const open = () => {
  searchQuery.value = '';
  searchResults.value = [];
  selectedIds.value = new Set();
  currentPage.value = 1;
  hasMore.value = false;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleEnroll = async () => {
  if (selectedIds.value.size === 0) return;

  isEnrolling.value = true;
  try {
    const response = await FollowUpSequencesAPI.enroll(
      props.sequenceId,
      Array.from(selectedIds.value)
    );
    const result = response.data;
    useAlert(
      t('CAMPAIGN.FOLLOW_UP.ENROLL.SUCCESS', {
        enrolled: result.enrolled || 0,
        skipped: result.skipped || 0,
      })
    );
    emit('enrolled');
    close();
  } catch {
    useAlert(t('CAMPAIGN.FOLLOW_UP.ENROLL.ERROR'));
  } finally {
    isEnrolling.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.FOLLOW_UP.ENROLL.TITLE')"
    width="xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <div class="space-y-4">
      <Input
        v-model="searchQuery"
        :placeholder="t('CAMPAIGN.FOLLOW_UP.ENROLL.SEARCH_PLACEHOLDER')"
        icon="i-lucide-search"
        @input="handleSearch"
      />

      <div v-if="selectedIds.size > 0" class="text-sm text-n-brand font-medium">
        {{
          t('CAMPAIGN.FOLLOW_UP.ENROLL.SELECTED', {
            count: selectedIds.size,
          })
        }}
      </div>

      <div class="max-h-80 overflow-y-auto space-y-1" @scroll="handleScroll">
        <Spinner v-if="isSearching" />

        <div
          v-else-if="searchResults.length === 0 && searchQuery.length >= 2"
          class="py-6 text-center text-sm text-n-slate-10"
        >
          {{ t('CAMPAIGN.FOLLOW_UP.ENROLL.EMPTY') }}
        </div>

        <button
          v-for="contact in searchResults"
          :key="contact.id"
          class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors"
          :class="
            selectedIds.has(contact.id)
              ? 'bg-n-brand/5 border border-n-brand'
              : 'hover:bg-n-alpha-2 border border-transparent'
          "
          @click="toggleContact(contact.id)"
        >
          <div
            class="w-8 h-8 rounded-full bg-n-alpha-3 flex items-center justify-center text-xs font-medium text-n-slate-12 shrink-0"
          >
            {{ (contact.name || '?').charAt(0).toUpperCase() }}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ contact.name || '-' }}
            </p>
            <p class="text-xs text-n-slate-10 truncate">
              {{ contact.phone_number || contact.email || '-' }}
            </p>
          </div>
          <span
            v-if="selectedIds.has(contact.id)"
            class="i-lucide-check w-4 h-4 text-n-brand shrink-0"
          />
        </button>

        <div v-if="isLoadingMore" class="py-3 flex justify-center">
          <Spinner />
        </div>

        <button
          v-else-if="hasMore && !isSearching"
          class="w-full py-2 text-center text-sm text-n-brand hover:text-n-brand/80"
          @click="loadMore"
        >
          {{ t('CAMPAIGN.FOLLOW_UP.ENROLL.LOAD_MORE') }}
        </button>
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button variant="faded" color="slate" @click="close">
          {{ t('CAMPAIGN.FOLLOW_UP.ENROLL.CANCEL') }}
        </Button>
        <Button
          :disabled="selectedIds.size === 0"
          :is-loading="isEnrolling"
          @click="handleEnroll"
        >
          {{ t('CAMPAIGN.FOLLOW_UP.ENROLL.CONFIRM') }}
        </Button>
      </div>
    </div>
  </Dialog>
</template>
